import React from 'react';
import { Button } from '@/components/ui/button';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { NavigationLink } from '../Navigation/NavigationLink';

export interface SubNavItem {
  label: string;
  href: string;
}

export interface SubNavSectionProps {
  title: string;
  items: SubNavItem[];
  activeItem?: string;
  isExpanded?: boolean;
  onToggle?: () => void;
  onItemClick?: (groupTitle: string, itemHref: string) => void;
  showBorder?: boolean;
  variant?: 'card' | 'sheet' | 'accordion';
  /** Custom link component for routing (e.g., React Router Link) */
  linkComponent?: React.ElementType;
}

export const SubNavSection: React.FC<SubNavSectionProps> = ({
  title,
  items,
  activeItem,
  isExpanded = false,
  onToggle,
  onItemClick,
  showBorder = false,
  variant = 'card',
  linkComponent
}) => {
  const headerId = React.useId();
  const regionId = React.useId();

  const renderNavItem = (item: SubNavItem) => {
    if (linkComponent) {
      return (
        <NavigationLink
          key={item.href}
          as={linkComponent}
          href={item.href}
          className={cn(
            "block w-full text-left font-normal px-2 py-2 text-sm rounded-md",
            "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
            activeItem === item.href && "text-sky-700 bg-sky-50"
          )}
          aria-current={activeItem === item.href ? 'page' : undefined}
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            onItemClick?.(title, item.href);
          }}
        >
          {item.label}
        </NavigationLink>
      );
    }
    
    return (
      <Button
        key={item.href}
        variant="ghost"
        className={cn(
          "w-full justify-start text-left font-normal px-2 py-2 h-auto text-sm rounded-md",
          "text-gray-700 hover:text-gray-900 hover:bg-gray-50",
          activeItem === item.href && "text-sky-700 bg-sky-50"
        )}
        type="button"
        aria-current={activeItem === item.href ? 'page' : undefined}
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.preventDefault();
          onItemClick?.(title, item.href);
        }}
      >
        {item.label}
      </Button>
    );
  };

  if (variant === 'card') {
    return (
      <div className="bg-white rounded-lg p-4">
        <h3 className="text-sm font-semibold text-sky-700 mb-3">{title}</h3>
        <div className="space-y-1">
          {items.map(renderNavItem)}
        </div>
      </div>
    );
  }

  // Sheet or Accordion variant with collapsible header
  return (
    <div className={cn(showBorder && "border-b border-gray-100")}> 
      <button
        type="button"
        onClick={onToggle}
        id={headerId}
        aria-expanded={isExpanded}
        aria-controls={regionId}
        className="w-full flex items-center justify-between py-3 px-2 text-left hover:bg-gray-50 rounded transition-colors"
      >
        <span className="text-sm font-medium text-gray-900">{title}</span>
        <ChevronDown 
          className={cn(
            "w-4 h-4 text-gray-400 transition-transform",
            isExpanded && "rotate-180"
          )}
        />
      </button>
      {isExpanded && (
        <div className="pb-3 space-y-1" id={regionId} role="region" aria-labelledby={headerId}>
          {items.map(renderNavItem)}
        </div>
      )}
    </div>
  );
};

SubNavSection.displayName = 'SubNavSection';
